/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { format } from 'date-fns';
import {
    SecurityHelper,
    StyledModal,
    ModalActions,
    ButtonGroup,
    Button,
} from 'sarsaparilla';
import { searchActions } from 'ui-search';
import * as tripPlannerActions from '../actions/tripPlanner';

const propTypes = {
    bookingData: PropTypes.object,
    preferences: PropTypes.object,
    dispatch: PropTypes.func,
    uiFilters: PropTypes.object,
};

const defaultProps = {
    preferences: {
        hide_intro: false,
        hide_booking_intro: false,
    },
};

export class TripPlannerBookModal extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            isBookModalOpen: false,
            doNotShowAgain: false,
        };

        this.getBookingPage = this.getBookingPage.bind(this);
        this.handleOnChange = this.handleOnChange.bind(this);
        this.onModalClose = this.onModalClose.bind(this);
        this.onModalBook = this.onModalBook.bind(this);
        this.onModalCancel = this.onModalCancel.bind(this);
        this.redirectToBookingPage = this.redirectToBookingPage.bind(this);
    }

    componentDidMount() {
        if (SecurityHelper.isLoggedIn()) {
            this.props.dispatch(tripPlannerActions.fetchUserPreferences());
        } else {
            const doNotShowAgainBookModalUserSetting = sessionStorage.getItem(
                'doNotShowAgainBookModalUserSetting'
            );
            if (doNotShowAgainBookModalUserSetting) {
                this.props.dispatch(
                    tripPlannerActions.setHideBookingIntro(
                        doNotShowAgainBookModalUserSetting
                    )
                );
            }
        }
    }

    UNSAFE_componentWillReceiveProps(nextProps) {
        if (
            JSON.stringify(nextProps.bookingData) !==
            JSON.stringify(this.props.bookingData)
        ) {
            if (nextProps.preferences.hide_booking_intro) {
                this.redirectToBookingPage(nextProps.bookingData);
            } else {
                this.setState({ isBookModalOpen: true });
            }
        }
    }

    onModalClose(isClickedBookButton) {
        // close the modal
        this.setState({ isBookModalOpen: false });

        if (isClickedBookButton) {
            if (this.state.doNotShowAgain) {
                this.props.dispatch(tripPlannerActions.setHideBookingIntro(true));

                if (SecurityHelper.isLoggedIn()) {
                    this.props.dispatch(tripPlannerActions.saveUserPreferences());
                } else {
                    sessionStorage.setItem('doNotShowAgainBookModalUserSetting', true);
                }
            }

            this.redirectToBookingPage(this.props.bookingData);
        }
    }

    onModalCancel() {
        this.onModalClose(false);
    }

    onModalBook() {
        this.onModalClose(true);
    }

    getBookingPage(item) {
        let targetUri;
        if (item.entity_type === 'campground') {
            targetUri = `/camping/campgrounds/${item.entity_id}/campsites`;
        } else if (item.entity_type === 'permit') {
            targetUri = `/permits/${item.entity_id}`;
        } else if (item.entity_type === 'vehiclepermit') {
            targetUri = `/vehiclepermits/${item.entity_id}`;
        } else if (item.entity_type === 'tour') {
            targetUri = `/ticket/${item.parent_id}/ticket/${item.entity_id}}`;
        } else if (item.entity_type === 'ticketfacility') {
            targetUri = `/ticket/facility/${item.entity_id}`;
        } else if (item.entity_type === 'timedentry_tour') {
            targetUri = `/timed-entry/${item.parent_id}/ticket/${item.entity_id}`;
        } else if (item.entity_type === 'timedentry') {
            targetUri = `/timed-entry/${item.entity_id}`;
        } else if (item.entity_type === 'activitypass') {
            targetUri = `/activitypass/${item.entity_id}`;
        } else if (item.entity_type === 'treepermit') {
            targetUri = `/tree-permits/${item.entity_id}`;
        } else {
            targetUri = `/${item.entity_type}/${item.entity_id}`;
        }

        return targetUri;
    }

    redirectToBookingPage(bookingData) {
        let bookingPageUri = this.getBookingPage(bookingData.item);
        if (bookingData.item.entity_type === 'campground') {
            // set and store search filters in session store
            this.applyCampingSearchFilters();
        }

        const params = [];
        if (bookingData.item.tripId) {
            params.push(`tripid=${bookingData.item.tripId}`);
        }

        if (bookingData.item.arrivalDate && bookingData.item.departureDate) {
            params.push(
                `arrivaldate=${format(bookingData.item.arrivalDate, 'yyyy-MM-dd')}`
            );
            params.push(
                `departuredate=${format(bookingData.item.departureDate, 'yyyy-MM-dd')}`
            );
        }

        if (bookingData.item.guestCounts) {
            if (bookingData.item.guestCounts.access) {
                params.push(`access=${bookingData.item.guestCounts.access}`);
            }
            if (bookingData.item.guestCounts.adult) {
                params.push(`adult=${bookingData.item.guestCounts.adult}`);
            }
            if (bookingData.item.guestCounts.senior) {
                params.push(`senior=${bookingData.item.guestCounts.senior}`);
            }
            if (bookingData.item.guestCounts.youth) {
                params.push(`youth=${bookingData.item.guestCounts.youth}`);
            }
        }

        if (params.length > 0) {
            bookingPageUri = `${bookingPageUri}?${encodeURI(params.join('&'))}`;
        }

        window.open(bookingPageUri, '_blank');
    }

    applyCampingSearchFilters() {
        this.props.dispatch(searchActions.resetSearchFiltersCampsites());

        Object.keys(this.props.uiFilters.campsiteTypes).forEach((key) => {
            this.props.dispatch(
                searchActions.setSearchFilter(
                    'campsiteTypes',
                    this.props.uiFilters.campsiteTypes[key]
                )
            );
        });
        Object.keys(this.props.uiFilters.campsiteEquipment).forEach((key) => {
            this.props.dispatch(
                searchActions.setSearchFilter(
                    'campsiteEquipment',
                    this.props.uiFilters.campsiteEquipment[key]
                )
            );
        });
        Object.keys(this.props.uiFilters.campsiteAmenities).forEach((key) => {
            this.props.dispatch(
                searchActions.setSearchFilter(
                    'campsiteAmenities',
                    this.props.uiFilters.campsiteAmenities[key]
                )
            );
        });
        Object.keys(this.props.uiFilters.campsiteElectricityHookup).forEach((key) => {
            this.props.dispatch(
                searchActions.setSearchFilter(
                    'campsiteElectricityHookup',
                    this.props.uiFilters.campsiteElectricityHookup[key]
                )
            );
        });
        Object.keys(this.props.uiFilters.campsiteVehicleLength).forEach((key) => {
            this.props.dispatch(
                searchActions.setSearchFilter(
                    'campsiteVehicleLength',
                    this.props.uiFilters.campsiteVehicleLength[key]
                )
            );
        });
        Object.keys(this.props.uiFilters.campsiteUseTypes).forEach((key) => {
            this.props.dispatch(
                searchActions.setSearchFilter(
                    'campsiteUseTypes',
                    this.props.uiFilters.campsiteUseTypes[key]
                )
            );
        });
    }

    handleOnChange(e) {
        this.setState({ doNotShowAgain: e.target.checked });
    }

    render() {
        return (
            <StyledModal
                isOpen={this.state.isBookModalOpen}
                onRequestClose={this.onModalCancel}
                role="complementary"
                ariaHideApp={false}
                contentLabel="Trip Planner Book"
            >
                <div className="trip-planner-book-modal-view">
                    <div className="rec-modal-content-wrap">
                        <h3>Continue to booking a reservation?</h3>
                        <p>
                            You will now be directed to the details page to check
                            availability and continue to booking.
                        </p>
                    </div>
                </div>
                <ModalActions>
                    <div className="trip-planner-do-not-show-again-input">
                        <label htmlFor="doNotShowAgain" className="rec-label-checkbox">
                            <input
                                className="rec-input-hide"
                                type="checkbox"
                                id="doNotShowAgain"
                                name="doNotShowAgain"
                                checked={!!this.state.doNotShowAgain}
                                onChange={this.handleOnChange}
                            />
                            <span className="rec-input-checkbox" />
                            Do not show again
                        </label>
                    </div>
                    <ButtonGroup>
                        <Button
                            appreance="tertiary"
                            title="Cancel"
                            aria-label="Cancel"
                            onClick={this.onModalCancel}
                        >
                            Cancel
                        </Button>
                        <Button
                            title="Continue to Booking"
                            aria-label="Continue to Booking"
                            onClick={this.onModalBook}
                        >
                            Continue to Booking
                        </Button>
                    </ButtonGroup>
                </ModalActions>
            </StyledModal>
        );
    }
}

TripPlannerBookModal.propTypes = propTypes;
TripPlannerBookModal.defaultProps = defaultProps;

function select(state) {
    return {
        uiFilters: state.tripPlanner.uiFilters,
        bookingData: state.tripPlanner.bookingData,
        preferences: state.tripPlanner.preferences,
    };
}

export default connect(select)(TripPlannerBookModal);
